import { useEffect, useState } from 'react'
import { Check, Copy, Ticket, X } from 'lucide-react'
import { barStyle, safeColor } from '@/lib/promoStyle'
import { copyCouponCode, formatThaiDate } from '@/lib/promoCoupon'
import type { ActiveCampaign, CampaignCoupon } from '@/services/campaignService'

function CouponCard({ coupon, color }: { coupon: CampaignCoupon; color: string }) {
  const [done, setDone] = useState(false)
  const until = coupon.expires_at ? `ใช้ได้ถึง ${formatThaiDate(coupon.expires_at)}` : 'ไม่มีวันหมดอายุ'
  return (
    <div className="flex overflow-hidden rounded-md border border-border bg-surface">
      {/* ฝั่งซ้าย: ส่วนลด (เหมือนตั๋วคูปอง) */}
      <div className="flex w-28 shrink-0 flex-col items-center justify-center px-2 py-4 text-center text-white" style={barStyle(color)}>
        <Ticket className="mb-1 h-5 w-5 opacity-80" />
        <p className="text-sm font-extrabold leading-tight">{coupon.title}</p>
      </div>
      {/* รอยปรุ */}
      <div className="relative w-0 border-l-2 border-dashed border-border">
        <span className="absolute -left-2 -top-2 h-4 w-4 rounded-full bg-white ring-1 ring-border" />
        <span className="absolute -bottom-2 -left-2 h-4 w-4 rounded-full bg-white ring-1 ring-border" />
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className="text-base font-extrabold tracking-wide text-text">{coupon.code}</p>
          <p className="mt-0.5 text-xs leading-5 text-text-muted">{coupon.detail}</p>
          <p className="mt-0.5 text-[11px] text-text-muted/80">{until}</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            if (await copyCouponCode(coupon.code)) {
              setDone(true)
              setTimeout(() => setDone(false), 1600)
            }
          }}
          className={`flex shrink-0 items-center gap-1.5 rounded-sm px-3 py-2 text-xs font-semibold transition ${done ? 'bg-emerald-50 text-emerald-600' : 'bg-accent text-white hover:bg-accent2'}`}
        >
          {done ? <><Check className="h-3.5 w-3.5" />คัดลอกแล้ว</> : <><Copy className="h-3.5 w-3.5" />คัดลอก</>}
        </button>
      </div>
    </div>
  )
}

// ปุ่มลอยมุมล่างซ้าย → หน้าต่าง "คูปองสำหรับคุณ" รวมคูปองของแคมเปญ (คัดลอกรหัสแล้วไปวางที่หน้าชำระเงิน)
export default function PromoFloating({ campaign }: { campaign: ActiveCampaign }) {
  const f = campaign.floating!
  const color = safeColor(campaign.color)
  const hideKey = `promoFloatHidden:${campaign.campaign_id}`   // ✕ = ย่อเป็นวงกลม (ไม่ได้ซ่อนถาวร) จำไว้ตลอดเซสชัน
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem(hideKey) === '1' } catch { return false }
  })

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      {/* ย่อแล้ว: เหลือปุ่มวงกลม กดแล้วการ์ดกลับมาที่มุมล่างซ้าย (จากนั้นกด "คลิกที่นี่" เพื่อดูคูปอง) */}
      {collapsed && !open && (
        <button
          type="button"
          onClick={() => {
            setCollapsed(false)
            try { sessionStorage.removeItem(hideKey) } catch { /* ข้าม */ }
          }}
          aria-label="แสดงการ์ดคูปอง"
          title="แสดงการ์ดคูปอง"
          className="fixed bottom-4 left-4 z-40 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl ring-4 ring-white/70 transition hover:scale-110"
          style={barStyle(color)}
        >
          <Ticket className="h-6 w-6" />
        </button>
      )}

      {!collapsed && !open && (
        <div className="fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)]">
          <div className="relative flex items-center gap-3 rounded-2xl py-3 pl-4 pr-3 text-white shadow-xl sm:gap-4 sm:pl-5 sm:pr-4" style={barStyle(color)}>
            <Ticket className="hidden h-9 w-9 shrink-0 opacity-90 sm:block" />
            <div className="min-w-0 leading-tight">
              <p className="text-sm font-bold sm:text-lg">{f.headline}</p>
              {f.sub && <p className="text-[11px] text-white/85 sm:text-xs">{f.sub}</p>}
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              style={{ color }}
              className="shrink-0 rounded-full bg-white px-4 py-2 text-xs font-bold shadow-sm transition hover:scale-105 sm:px-6 sm:text-sm"
            >
              {f.button || 'คลิกที่นี่'}
            </button>
            <button
              type="button"
              aria-label="ย่อเป็นปุ่มวงกลม"
              onClick={() => {
                setCollapsed(true)
                try { sessionStorage.setItem(hideKey, '1') } catch { /* ข้าม */ }
              }}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={() => setOpen(false)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 px-5 py-4 text-white" style={barStyle(color)}>
              <div>
                {campaign.banner?.main && <p className="text-xs font-semibold tracking-wider text-white/80">{[campaign.banner.top, campaign.banner.main].filter(Boolean).join(' ')}</p>}
                <h3 className="text-lg font-bold">คูปองสำหรับคุณ</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="ปิด" className="rounded-full p-1 text-white/80 transition hover:bg-white/20 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 p-5">
              {campaign.coupons.length === 0 && <p className="py-4 text-center text-sm text-text-muted">ยังไม่มีคูปองที่ใช้ได้ในตอนนี้</p>}
              {campaign.coupons.map((c) => <CouponCard key={c.code} coupon={c} color={color} />)}
              {campaign.coupons.length > 0 && (
                <p className="pt-1 text-xs leading-5 text-text-muted">
                  ใช้ได้ 1 รหัสต่อ 1 คำสั่งซื้อ · คัดลอกรหัสแล้วไปวางที่ช่อง "มีคูปองส่วนลด?" ตอนชำระเงิน (ระบบจะกรอกให้อัตโนมัติ)
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
